import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useLazyGetUserSocialIntegrationsQuery } from '@app/api/SocialIntegrations';
import { WARNING } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import { useGetSalesFunnelQuery } from '@app/services/rtkQuery/SalesFunnels/api';
import type { NFunnels, NSmallGlobalEntities } from '@app/types';

export const useViewSalesFunnelPageHeaderEffects = () => {
  const [query] = useSearchParams();
  const id = query.get('id');

  const [isWriteFirstModalOpen, setIsWriteFirstModalOpen] = useState(false);
  const [isDeletingModalOpen, setIsDeletingModalOpen] = useState(false);
  const [userSocialIntegrations, setUserSocialIntegrations] = useState<
    NSmallGlobalEntities.ModelItem[]
  >([]);
  const [currentSalesFunnel, setCurrentSalesFunnel] =
    useState<NFunnels.SalesFunnelDto | null>(null);

  const { activeBranchId } = useUserPrint();

  const { showNotification } = useContext(NotificationContext);

  const [getUserSocialIntegrations] = useLazyGetUserSocialIntegrationsQuery();

  const { data: currentSalesFunnelData, error: salesFunnelError } =
    useGetSalesFunnelQuery(
      {
        id,
        ActiveBranchId: activeBranchId,
      },
      {
        skip: !activeBranchId || !id,
      }
    );

  useEffect(() => {
    const getInitialData = async () => {
      try {
        const integrationsResult = await getUserSocialIntegrations({
          ActiveBranchId: activeBranchId,
        }).unwrap();
        setUserSocialIntegrations(integrationsResult);
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      }
    };

    getInitialData();
  }, [activeBranchId, getUserSocialIntegrations, showNotification]);

  useEffect(() => {
    if (currentSalesFunnelData) {
      setCurrentSalesFunnel(currentSalesFunnelData);
    }
  }, [currentSalesFunnelData, setCurrentSalesFunnel]);

  useEffect(() => {
    if (salesFunnelError) {
      showNotification(handleCatchError(salesFunnelError));
    }
  }, [salesFunnelError, showNotification]);

  return {
    isWriteFirstModalOpen,
    setIsWriteFirstModalOpen,
    userSocialIntegrations,
    isDeletingModalOpen,
    setIsDeletingModalOpen,
    showNotification,
    activeBranchId,
    currentSalesFunnel,
  };
};
